import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { wishlistApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLocalization } from '../contexts/LocalizationContext';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { countryCode } = useLocalization();

  // Check if user is logged in
  const isLoggedIn = () => {
    return !!localStorage.getItem('accessToken');
  };

  // Fetch wishlist from backend
  const fetchWishlist = async () => {
    if (!isLoggedIn()) {
      setWishlist({ items: [] });
      setWishlistCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const data = await wishlistApi.getWishlist(token, countryCode);
      setWishlist(data);
      setWishlistCount(data.items?.length || 0);
    } catch (error) {
      // Silently handle 401 (unauthorized) errors for non-logged-in users
      if (error.response?.status !== 401) {
        console.error('Error fetching wishlist:', error);
      }
      setWishlist({ items: [] });
      setWishlistCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Add item to wishlist
  const addToWishlist = async (productId) => {
    if (!isLoggedIn()) {
      toast({
        title: t('wishlistToasts.loginRequired'),
        description: t('wishlistToasts.loginRequiredDescription'),
        variant: 'destructive',
      });
      return false;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await wishlistApi.addToWishlist(productId, token);
      await fetchWishlist();
      toast({
        title: t('wishlistToasts.addedTitle'),
        description: t('wishlistToasts.addedDescription'),
      });
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      
      // Handle error message properly
      let errorMsg = t('wishlistToasts.addFailed');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
      return false;
    }
  };

  // Remove item from wishlist
  const removeFromWishlist = async (productId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await wishlistApi.removeFromWishlist(productId, token);
      await fetchWishlist();
      toast({
        title: t('wishlistToasts.removedTitle'),
        description: t('wishlistToasts.removedDescription'),
      });
      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      
      // Handle error message properly
      let errorMsg = t('wishlistToasts.removeFailed');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
      return false;
    }
  };

  // Toggle wishlist (add or remove)
  const toggleWishlist = async (productId) => {
    const isInWishlist = wishlist.items?.some(item => item.productId === productId);
    if (isInWishlist) {
      return await removeFromWishlist(productId);
    } else {
      return await addToWishlist(productId);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.items?.some(item => item.productId === productId);
  };

  useEffect(() => {
    fetchWishlist();
  }, [countryCode]); // Refetch when country changes

  const value = {
    wishlist,
    wishlistCount,
    loading,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    refreshWishlist: fetchWishlist,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};